import styled, { css } from 'styled-components'

import { mq, sizes } from '../../../theme'
import Icon from '../Icon'
import Picture from '../Picture'

type CommonProps = {
  $checked?: boolean
  $disabled?: boolean
  $image?: boolean
}

export enum ToggleButtonVariant {
  default = 'default',
  quiz = 'quiz',
}

export const Label = styled.p<CommonProps>`
  ${({ theme }) => theme.textStyles.textSmallStrong};
  font-weight: bold;
  color: ${({ theme }) => theme.colors.bismark};
  transition: color 0.3s ease;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  ${({ $checked, theme }) =>
    $checked &&
    css`
      color: ${theme.colors.pureWhite};
    `};

  ${({ $disabled }) =>
    $disabled &&
    css`
      opacity: 0.4;
    `}
`

export const LabelTitle = styled.span<{
  $icon: boolean
  $variant?: ToggleButtonVariant
}>`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: ${({ $icon }) => $icon && 'column'};
  text-align: center;

  ${({ $variant }) =>
    $variant === ToggleButtonVariant.quiz &&
    css`
      flex-direction: row-reverse;
      gap: 1rem;
    `};

  @media ${mq(sizes.tablet)} {
    flex-direction: row;

    ${({ $variant }) =>
      $variant === ToggleButtonVariant.quiz &&
      css`
        flex-direction: row-reverse;
      `};
  }
`

export const Subtitle = styled.span`
  ${({ theme }) => theme.textStyles.textXSmall};
`

export const StyledIcon = styled(Icon)<{ $variant?: ToggleButtonVariant }>`
  pointer-events: none;

  @media ${mq(sizes.tablet)} {
    margin-right: 1rem;

    ${({ $variant }) =>
      $variant === ToggleButtonVariant.quiz &&
      css`
        margin-right: 0;
      `};
  }
`
export const StyledPicture = styled(Picture)``

export const Wrapper = styled.label<
  CommonProps & { $colorChecked?: string; $variant?: ToggleButtonVariant }
>`
  --bg-color: ${({ theme }) => theme.colors.pureWhite};
  border: 1px solid ${({ theme }) => theme.colors.pattensBlue};
  border-radius: 1rem;
  min-height: 5rem;
  line-height: 5rem;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  transition: background-color 0.3s ease;
  background: var(--bg-color, white);

  &:hover {
    background-color: ${({ theme }) => theme.colors.arapawa};
    border-color: ${({ theme }) => theme.colors.arapawa};
    ${Label} {
      color: ${({ theme }) => theme.colors.pureWhite};
    }
  }

  ${({ $image }) =>
    !$image &&
    css`
      padding: 0 2rem;
    `};

  ${({ $image, theme }) =>
    $image &&
    css`
      height: auto;
      border-radius: 50%;
      width: auto;
      padding: 2rem;
      & picture,
      & img {
        display: block;
      }
      & picture {
        max-width: 6rem;
        aspect-ratio: 1;
      }
      & img {
        width: 100%;
        height: 100%;
        object-fit: contain;
      }
      &:hover {
        background-color: transparent;
        outline: 2px solid ${theme.colors.ruby};
        border-color: ${theme.colors.ruby};
      }
      @media ${mq(sizes.tablet)} {
        & picture {
          max-width: 8rem;
        }
      }
    `};

  ${({ $checked, theme, $image, $colorChecked }) =>
    $checked && $image
      ? css`
          outline: 2px solid ${theme.colors.ruby};
          &,
          &:hover {
            border-color: ${theme.colors.ruby};
            background-color: var(--bg-color, white);
          }
        `
      : $checked &&
        css`
          &,
          &:hover {
            background-color: ${$colorChecked
              ? $colorChecked
              : theme.colors.arapawa};
            border-color: ${$colorChecked
              ? $colorChecked
              : theme.colors.arapawa};
          }
        `};

  ${({ $disabled, $variant, $checked }) =>
    $disabled && $variant && $checked
      ? css`
          opacity: 1;
          pointer-events: none;

          & p {
            opacity: 1;
          }
        `
      : $disabled &&
        css`
          opacity: 0.4;
          pointer-events: none;
        `}
`

export const Checkbox = styled.input`
  opacity: 0;
  position: absolute;
  &:focus ~ ${Label}, &:focus ~ ${StyledPicture} {
    outline: 0.1rem dotted #000;
  }
  &:focus:not(:focus-visible)
    ~ ${Label},
    &:focus:not(:focus-visible)
    ~ ${StyledPicture} {
    outline: none;
  }
  &:focus-visible ~ ${Label}, &:focus ~ ${StyledPicture} {
    outline: 0.1rem dotted #000;
  }
`
